import type { TemplateDefinition } from '../../common/overview/models/templateDefinition.types';
import { freezeMandatorySystemFields } from '../../common/overview/models/freezeMandatory';

/**
 * The PR Template kind.
 *
 * Four steps, no group heading -- drawn as the design16 prototype draws them (2026-10-19): steps across the top,
 * the system fields as preview tiles, Template Information with an optional Business Unit, Division and Department.
 * design21 (2026-10-22): a required Category multi-select is back in Template Information, Priority takes the header
 * Location's place, the record's own Status closes Purchase Request Details, and TEMPLATE CODE leads the list.
 *
 * `/templates` redirects here, so this is the screen a user meets first.
 */
export const prTemplateDefinition: TemplateDefinition = {
  kind: 'PURCHASE_REQUEST',
  feature: 'PR Template',
  path: 'pr',
  icon: 'pi-clipboard',
  title: 'Purchase Request Templates',
  subtitle:
    'Create and manage reusable Purchase Request templates Initiators fill in when raising a purchase request.',
  searchPlaceholder: 'Search templates by name',
  allStatusLabel: 'All Status',
  createSubtitle:
    'Configure a reusable purchase request template that Initiators will use when raising a Purchase Request.',
  savedMessage: 'Initiators can now pick this template when raising a purchase request.',
  fieldRemovalConsequence: 'Initiators will no longer see it when raising a purchase request.',
  deactivateConsequence:
    'Initiators will no longer be able to select "{name}" for new records. Existing records are unaffected.',
  activateConsequence: '"{name}" will become available for Initiators to select again.',
  blankNameFallback: 'Purchase request template',
  collectsCategory: false,
  /* design21 (2026-10-22): Category is the Category master's rows again, several and required ("Category is required."). */
  collectsMasterCategories: true,
  requiredInformation: ['categoryIds'],
  /* A draft may still be saved without one; the server holds it to a Category on its next final save. */
  draftSkipsRequiredInformation: true,
  preservesCategories: true,
  /* Mapped to the hierarchy, and offered on Purchase Requests by it (NB-355) -- one Business Unit, Division and
     Department at most, each optional. */
  collectsHierarchy: true,
  /* The entity's seeded default is neither edited nor deleted from the list; templates created or duplicated beside it are. */
  lockedTemplateName: 'Default Purchase Request',
  hierarchyOptional: true,
  /* The code beside the name, and field names unique across the template (2026-10-14). */
  updateNamesCode: true,
  uniqueFieldNames: true,
  overviewSecondValue: 'fieldCount',
  namePlaceholder: 'e.g. Standard Purchase Request',
  descriptionPlaceholder: 'What is this template used for?',
  informationHint: 'Shown to Initiators when they pick a template.',
  exportFormats: ['xlsx', 'csv'],

  /* The design16 prototype (2026-10-19). */
  topStepper: true,
  designFooter: true,
  draftSavesInPlace: true,
  /* Save as Draft returns to the list, on any step (2026-10-22). */
  draftReturnsToList: true,
  /* Save Template with the Status toggle off saves Inactive; only Save as Draft saves Draft (2026-10-22). */
  toggleOffSavesInactive: true,
  designInformation: true,
  systemFieldTiles: true,
  draftLabel: 'Draft',
  pageSizes: [5, 10, 25, 50],
  defaultPageSize: 25,
  importTemplateColumns: ['Template Name', 'Template ID', 'Category', 'Created By', 'Last Modified', 'Status', 'Description'],
  exportSubtitle: 'Choose the rows and columns to include in the exported file.',
  deleteMessage: 'This template will be permanently removed. Records already created from it are unaffected.',
  overviewMinimal: true,
  filterHidesHierarchy: true,
  savedDetail: {
    createSubtitle: 'Your Purchase Request template is ready to use.',
    editSubtitle: 'Your changes to this template have been saved.',
    createInfo: 'Initiators can now pick this template when raising a purchase request.',
    editInfo:
      'Your changes are live. New purchase requests raised from this template will use the updated configuration; '
      + 'existing requests are not affected.',
  },

  /* The prototype's list: TEMPLATE CODE (design21, fixed), TEMPLATE NAME, CATEGORY, CREATED BY, LAST MODIFIED, STATUS, ACTIONS. */
  codeColumn: true,
  columns: [
    { key: 'templateCategory', header: 'CATEGORY', label: 'Category', minWidth: 150, renderer: 'categoryPill', sortable: true, filter: { comparison: 'Is', kind: 'text' } },
    { key: 'createdBy', header: 'CREATED BY', label: 'Created By', minWidth: 180, renderer: 'avatar', sortable: true, filter: { comparison: 'Contains', kind: 'text' } },
    { key: 'modifiedDate', header: 'LAST MODIFIED', label: 'Last Modified', minWidth: 150, renderer: 'text', sortable: true, filter: { comparison: 'Is after', kind: 'text' } },
    { key: 'statusName', header: 'STATUS', label: 'Status', minWidth: 130, renderer: 'statusPill', sortable: true, filter: { comparison: 'Is', kind: 'choice' } },
  ],

  /* What Template Information collects, listed under it in View -> Sections. */
  informationFields: [
    { label: 'Template Name', type: 'Short text', required: true },
    { label: 'Category', type: 'Multi-select Dropdown', required: true },
    { label: 'Business Unit', type: 'Dropdown', required: false },
    { label: 'Division', type: 'Dropdown', required: false },
    { label: 'Department', type: 'Dropdown', required: false },
    { label: 'Description', type: 'Long text', required: false },
    { label: 'Status', type: 'Toggle', required: false },
  ],

  steps: freezeMandatorySystemFields([
    {
      sectionKey: 'template-information',
      label: 'Template Information',
      icon: 'pi-info-circle',
      hint: 'Name the template, describe its purpose and pick the category it applies to.',
      fields: [],
    },
    {
      sectionKey: 'pr-details',
      label: 'Purchase Request Details',
      icon: 'pi-file',
      hint: 'Set the Purchase Request header details Initiators complete when using this template.',
      panelHint:
        'Predefined system fields for every Purchase Request. Configure whether each is enabled and required.',
      fields: [
        { fieldKey: 'pr-number', label: 'Purchase Request Number', fieldType: 'Short text', helperText: 'Auto-generated', mandatory: true },
        { fieldKey: 'business-unit', label: 'Business Unit', fieldType: 'Dropdown', helperText: 'Auto-filled from user profile', mandatory: true },
        { fieldKey: 'division', label: 'Division', fieldType: 'Dropdown', helperText: 'Auto-filled from user profile', mandatory: true },
        { fieldKey: 'department', label: 'Department', fieldType: 'Dropdown', helperText: 'Auto-filled from user profile', mandatory: true },
        { fieldKey: 'pr-title', label: 'Purchase Request Title', fieldType: 'Short text', helperText: 'Enter Purchase Request Title', mandatory: true },
        /* design21 (2026-10-22): Priority in the header Location's place -- optional, and the request's Priority. */
        { fieldKey: 'priority', label: 'Priority', fieldType: 'Dropdown', helperText: 'Low, Medium, High, Urgent', mandatory: false, options: ['Low', 'Medium', 'High', 'Urgent'] },
        { fieldKey: 'budget-reference', label: 'Budget Reference', fieldType: 'Short text', helperText: 'Enter Budget Reference', mandatory: true },
        { fieldKey: 'delivery-date', label: 'Delivery Date', fieldType: 'Date', helperText: 'Select Delivery Date', mandatory: true },
        { fieldKey: 'description', label: 'Purchase Request Description', fieldType: 'Long text', helperText: 'Enter Purchase Request Description', mandatory: true },
        { fieldKey: 'remark', label: 'Remark', fieldType: 'Long text', helperText: 'Enter Remark', mandatory: false },
        /* The request's own Status (design21): frozen on; the form draws it as its Status switch, not as a field. */
        { fieldKey: 'status', label: 'Status', fieldType: 'Toggle', helperText: 'Active / Inactive · default Active', mandatory: true },
      ],
    },
    {
      sectionKey: 'line-items',
      label: 'Line Items',
      icon: 'pi-list',
      hint: 'Choose which line-item fields Initiators fill in for each requested product.',
      panelHint:
        'Add the products or materials required in this purchase request. These fields repeat for every item the Initiator adds.',
      fields: [
        { fieldKey: 'location', label: 'Location', fieldType: 'Dropdown', helperText: 'Select Location', mandatory: true },
        { fieldKey: 'category', label: 'Category', fieldType: 'Dropdown', helperText: 'Select Category', mandatory: true },
        { fieldKey: 'product', label: 'Product', fieldType: 'Search and Select', helperText: 'Search product by code or name', mandatory: true },
        { fieldKey: 'purchase-group', label: 'Purchase Group', fieldType: 'Short text', helperText: 'Auto-filled from product', mandatory: true },
        { fieldKey: 'cost-center-code', label: 'Cost Center Code', fieldType: 'Short text', helperText: 'Auto-filled from product', mandatory: true },
        { fieldKey: 'quantity', label: 'Quantity', fieldType: 'Number', helperText: '1 · UoM', mandatory: true },
      ],
    },
    {
      sectionKey: 'custom-fields',
      label: 'Custom Fields',
      icon: 'pi-plus-circle',
      hint: 'Add any additional fields specific to this purchase request template.',
      panelHint: 'Any additional fields specific to this purchase request template.',
      fields: [],
    },
  ]),
  /* Read an older save's wording as today's (the migration rewrites stored defaults; this covers a stale read). */
  legacyWording: (text) =>
    ({
      'PR Details': 'Purchase Request Details',
      'Line items': 'Line Items',
      'Custom fields': 'Custom Fields',
      'PR Title': 'Purchase Request Title',
    })[text] ?? text,
};

